/* Presentation enhancement only: follows existing selected stock/category tabs.
   It observes attributes/classes and never cancels, dispatches, or replaces events. */
(() => {
  'use strict';

  const categorySelector = '.je-ghost__tab:not(.je-ghost__tab--deals)';

  function updateLens(group) {
    const isGhostTabs = group.classList.contains('je-ghost__tabs');
    const selected = isGhostTabs
      // A visible hierarchy is the current visual context.  Deals is an
      // independent boolean filter and is deliberately excluded from this lens.
      ? group.querySelector(`${categorySelector}[aria-expanded="true"]`) || group.querySelector(`${categorySelector}[aria-current="true"]`)
      : group.querySelector('.cmain-tab.is-active, .cmain-tab[aria-selected="true"]');
    if (!selected || !group.isConnected) {
      if (group.classList.contains('je-filter-lens-ready')) group.classList.remove('je-filter-lens-ready');
      return;
    }
    group.style.setProperty('--je-filter-lens-left', `${selected.offsetLeft}px`);
    group.style.setProperty('--je-filter-lens-top', `${selected.offsetTop}px`);
    group.style.setProperty('--je-filter-lens-width', `${selected.offsetWidth}px`);
    group.style.setProperty('--je-filter-lens-height', `${selected.offsetHeight}px`);
    if (!group.classList.contains('je-filter-lens-ready')) group.classList.add('je-filter-lens-ready');
  }

  function bind(group) {
    if (group.dataset.jeFilterLensBound === 'true') return;
    group.dataset.jeFilterLensBound = 'true';
    let queued = false;
    const refresh = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        updateLens(group);
      });
    };
    group.addEventListener('click', refresh);
    group.addEventListener('keydown', refresh);
    group.addEventListener('scroll', refresh, { passive: true });
    new MutationObserver(refresh).observe(group, {
      subtree: true,
      attributes: true,
      childList: true,
      attributeFilter: ['class', 'aria-selected', 'aria-expanded', 'aria-current', 'hidden']
    });
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(refresh);
      observer.observe(group);
      group.querySelectorAll('.cmain-tab, .je-ghost__tab').forEach(tab => observer.observe(tab));
    }
    refresh();
  }

  // je-lens-targeted-20261020: discover new groups in the added subtree only.
  // Existing groups already observe their own selection, children and size.
  function scan(root = document) {
    if (root.nodeType !== 1 && root.nodeType !== 9) return;
    if (root.matches?.('.cmain-tabs, .je-ghost__tabs')) bind(root);
    root.querySelectorAll('.cmain-tabs, .je-ghost__tabs').forEach(bind);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  else scan();
  window.addEventListener('resize', () => document.querySelectorAll('.cmain-tabs, .je-ghost__tabs').forEach(updateLens), { passive: true });
  new MutationObserver(records => {
    for (const record of records) for (const node of record.addedNodes) scan(node);
  }).observe(document.documentElement, { childList: true, subtree: true });
})();

/* Native details provides no-JS navigation. Only add dismissal behaviour. */
(() => {
  const menu = document.querySelector('[data-je-photo-menu]');
  if (!menu) return;
  document.addEventListener('click', event => {
    if (menu.open && !menu.contains(event.target)) menu.open = false;
  });
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && menu.open) {
      menu.open = false;
      menu.querySelector('summary').focus();
    }
  });
  menu.addEventListener('click', event => {
    if (event.target.closest('a')) menu.open = false;
  });
})();

/* Glass select presentation. Original selects retain values, form ownership,
   option data and every existing input/change listener. */
(() => {
  'use strict';
  if (window.jeGlassSelects) return;
  window.jeGlassSelects = true;
  const states = new Map();
  let current = null, sequence = 0, queued = false;
  const arrow = '<svg viewBox="0 0 12 8" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="m1 1 5 5 5-5"/></svg>';
  const labelFor = select => select.getAttribute('aria-label') ||
    (select.getAttribute('aria-labelledby') || '').split(/\s+/).map(id => document.getElementById(id)?.textContent || '').join(' ').trim() ||
    (select.matches('.sorting-filter__select') ? 'Sort' : '') ||
    Array.from(new Set(Array.from(select.labels || []).map(label => {
      const copy = label.cloneNode(true);
      copy.querySelectorAll('select,.je-select,svg').forEach(node => node.remove());
      return copy.textContent.trim();
    }))).join(' ') || select.name.replace(/_/g, ' ') || 'Choose an option';
  const optionsFor = select => Array.from(select.options).map((option, index) => ({
    index, text: option.label || option.textContent.trim(), value: option.value,
    disabled: option.disabled || option.parentElement.disabled === true,
    hidden: option.hidden || option.parentElement.hidden,
    group: option.parentElement.tagName === 'OPTGROUP' ? option.parentElement.label : ''
  }));
  const enabled = state => state.options.filter(option => !option.disabled && !option.hidden);

  function place(state) {
    if (current !== state) return;
    const rect = state.button.getBoundingClientRect();
    if (!rect.width || !rect.height) { close(false); return; }
    const viewport = window.visualViewport;
    const leftEdge = viewport?.offsetLeft || 0, topEdge = viewport?.offsetTop || 0;
    const width = viewport?.width || document.documentElement.clientWidth;
    const height = viewport?.height || window.innerHeight;
    if (rect.bottom <= topEdge || rect.top >= topEdge + height || rect.right <= leftEdge || rect.left >= leftEdge + width) { close(false); return; }
    const margin = 10, gap = 7;
    const below = topEdge + height - rect.bottom - margin - gap;
    const above = rect.top - topEdge - margin - gap;
    const upward = below < 200 && above > below;
    const available = Math.max(44, upward ? above : below);
    const panelScale = state.panel.getBoundingClientRect().width / state.panel.offsetWidth || 1;
    state.panel.style.width = `${Math.min(Math.max(rect.width, 260), width - margin * 2) / panelScale}px`;
    state.panel.style.maxHeight = `${Math.min(360, available) / panelScale}px`;
    const panelHeight = state.panel.getBoundingClientRect().height;
    const panelWidth = state.panel.getBoundingClientRect().width;
    state.panel.style.left = `${Math.max(leftEdge + margin, Math.min(rect.left, leftEdge + width - panelWidth - margin)) / panelScale}px`;
    state.panel.style.top = `${Math.max(topEdge + margin, upward ? rect.top - gap - panelHeight : rect.bottom + gap) / panelScale}px`;
  }

  function highlight(state, index, scroll = true) {
    state.active = index;
    for (const node of state.panel.querySelectorAll('[role="option"]')) {
      const active = Number(node.dataset.index) === index;
      node.classList.toggle('is-focused', active);
      node.setAttribute('aria-selected', String(Number(node.dataset.index) === state.select.selectedIndex));
    }
    const node = state.panel.querySelector(`[data-index="${index}"]`);
    if (current === state && node) {
      state.button.setAttribute('aria-activedescendant', node.id);
      if (scroll) {
        const top = node.offsetTop, bottom = top + node.offsetHeight;
        if (top < state.panel.scrollTop + 8) state.panel.scrollTop = top - 8;
        else if (bottom > state.panel.scrollTop + state.panel.clientHeight - 8) state.panel.scrollTop = bottom - state.panel.clientHeight + 8;
      }
    }
  }

  function sync(state, styleSnapshot) {
    const { select, button, wrapper, panel } = state;
    const option = select.options[select.selectedIndex];
    const text = option?.label || option?.textContent.trim() || 'Choose an option';
    if (state.text.textContent !== text) state.text.textContent = text;
    button.disabled = select.disabled;
    button.setAttribute('aria-label', labelFor(select));
    panel.setAttribute('aria-label', labelFor(select));
    button.setAttribute('aria-required', String(select.required));
    button.setAttribute('aria-invalid', select.getAttribute('aria-invalid') || String(state.invalidReported === true && !select.validity.valid));
    button.title = text;
    const nativeStyle = styleSnapshot || getComputedStyle(select);
    wrapper.hidden = select.hidden || nativeStyle.display === 'none';
    if (wrapper.style.gridArea !== nativeStyle.gridArea) wrapper.style.gridArea = nativeStyle.gridArea;
    button.classList.toggle('has-selection', select.selectedIndex > 0 && select.value !== '');
    const options = optionsFor(select);
    const signature = JSON.stringify(options);
    if (signature !== state.signature) {
      state.signature = signature; state.options = options;
      const fragment = document.createDocumentFragment();
      let group = '';
      for (const item of options) {
        if (item.hidden) continue;
        if (item.group && item.group !== group) {
          const heading = document.createElement('div');
          heading.className = 'je-select__group'; heading.textContent = item.group;
          heading.setAttribute('role', 'presentation'); fragment.append(heading);
        }
        group = item.group;
        const row = document.createElement('div');
        row.className = 'je-select__option'; row.id = `${panel.id}-option-${item.index}`;
        row.dataset.index = item.index; row.setAttribute('role', 'option');
        row.setAttribute('aria-disabled', String(item.disabled));
        row.textContent = item.text; fragment.append(row);
      }
      panel.replaceChildren(fragment);
    }
    if (current === state && (button.disabled || wrapper.hidden)) close(false);
    highlight(state, current === state && enabled(state).some(item => item.index === state.active) ? state.active : select.selectedIndex, false);
    if (current === state) place(state);
  }

  function close(restoreFocus = false) {
    if (!current) return;
    const state = current; current = null;
    if (typeof state.panel.hidePopover === 'function' && state.panel.matches(':popover-open')) state.panel.hidePopover();
    state.panel.hidden = true;
    state.button.setAttribute('aria-expanded', 'false');
    state.button.removeAttribute('aria-activedescendant');
    if (restoreFocus && state.button.isConnected) state.button.focus({ preventScroll: true });
  }

  function open(state) {
    if (state.button.disabled) return;
    close(false); sync(state);
    current = state;
    // Keep popovers opened from a modal inside that modal's inert boundary.
    const host = state.select.closest('dialog[open]') || document.body;
    if (state.panel.parentElement !== host) host.append(state.panel);
    state.panel.hidden = false;
    if (typeof state.panel.showPopover === 'function') state.panel.showPopover();
    state.button.setAttribute('aria-expanded', 'true');
    place(state);
    const active = enabled(state).find(item => item.index === state.select.selectedIndex) || enabled(state)[0];
    if (active) highlight(state, active.index);
  }

  function commit(state, index, restoreFocus = true) {
    const option = state.options.find(item => item.index === index && !item.disabled && !item.hidden);
    if (!option) { close(restoreFocus); return; }
    const changed = state.select.selectedIndex !== index;
    state.select.selectedIndex = index;
    close(restoreFocus); sync(state);
    if (changed) {
      state.select.dispatchEvent(new Event('input', { bubbles: true }));
      state.select.dispatchEvent(new Event('change', { bubbles: true }));
      schedule();
    }
  }

  function keydown(event, state) {
    const key = event.key;
    if (key === 'Tab') { if (current === state) commit(state, state.active, false); return; }
    if (key === 'Escape') {
      if (current === state) { event.preventDefault(); event.stopPropagation(); close(true); }
      return;
    }
    if (event.ctrlKey || event.metaKey || (event.altKey && !['ArrowDown', 'ArrowUp'].includes(key))) return;
    if (['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' '].includes(key)) {
      event.preventDefault(); event.stopPropagation();
      if (current !== state) { open(state); if (key !== 'Home' && key !== 'End') return; }
      else if (key === 'Enter' || key === ' ') { commit(state, state.active); return; }
      const list = enabled(state);
      if (!list.length) return;
      const index = list.findIndex(item => item.index === state.active);
      const next = key === 'Home' ? 0 : key === 'End' ? list.length - 1 : Math.max(0, Math.min(list.length - 1, index + (key === 'ArrowUp' ? -1 : 1)));
      highlight(state, list[next].index);
    } else if (key.length === 1) {
      event.preventDefault(); event.stopPropagation();
      if (current !== state) open(state);
      const now = performance.now();
      state.typed = now - state.typedAt > 700 ? key : state.typed + key;
      state.typedAt = now;
      const search = [...state.typed].every(char => char.toLowerCase() === key.toLowerCase()) ? key : state.typed;
      const list = enabled(state), start = list.findIndex(item => item.index === state.active);
      const ordered = search.length === 1 ? [...list.slice(start + 1), ...list.slice(0, start + 1)] : list;
      const found = ordered.find(item => item.text.toLocaleLowerCase().startsWith(search.toLocaleLowerCase()));
      if (found) highlight(state, found.index);
    }
  }

  function bind(select, styleSnapshot) {
    if (states.has(select) || select.multiple || select.size > 1 || select.hasAttribute('data-je-native-select') || select.hidden || (styleSnapshot || getComputedStyle(select)).display === 'none') return;
    const wrapper = document.createElement('span');
    wrapper.className = 'je-select';
    if (select.matches('.cmain-select,.sort-select')) wrapper.classList.add('je-select--collection');
    if (select.matches('.je-ghost__select')) wrapper.classList.add('je-select--ghost');
    if (select.matches('.deals-sort-select')) wrapper.classList.add('je-select--deals-sort');
    if (select.matches('.variant-select,.variant-option__select,.je-sticky-variant')) wrapper.classList.add('je-select--variant');
    const button = document.createElement('button');
    button.type = 'button'; button.className = `${select.className} je-select__trigger`;
    button.setAttribute('role', 'combobox'); button.setAttribute('aria-haspopup', 'listbox'); button.setAttribute('aria-expanded', 'false');
    button.innerHTML = '<span class="je-select__text"></span>' + arrow;
    const panel = document.createElement('div');
    panel.id = `je-select-list-${++sequence}`; panel.className = 'je-select__menu';
    panel.setAttribute('role', 'listbox'); panel.setAttribute('aria-label', labelFor(select));
    if (typeof panel.showPopover === 'function') panel.setAttribute('popover', 'manual');
    panel.hidden = true; button.setAttribute('aria-controls', panel.id);
    button.firstElementChild.id = `${panel.id}-value`;
    const description = select.getAttribute('aria-describedby');
    button.setAttribute('aria-describedby', `${panel.id}-value${description ? ' ' + description : ''}`);
    select.before(wrapper); wrapper.append(select, button);
    select.classList.add('je-select__native'); select.tabIndex = -1; select.setAttribute('aria-hidden', 'true');
    const state = { select, wrapper, button, panel, text: button.firstElementChild, options: [], signature: '', active: -1, typed: '', typedAt: 0 };
    states.set(select, state); document.body.append(panel);
    button.addEventListener('click', () => current === state ? close(false) : open(state));
    button.addEventListener('keydown', event => keydown(event, state));
    panel.addEventListener('pointerdown', event => { if (event.pointerType === 'mouse') event.preventDefault(); });
    panel.addEventListener('pointermove', event => {
      const row = event.target.closest('[role="option"]');
      if (row && row.getAttribute('aria-disabled') !== 'true') highlight(state, Number(row.dataset.index), false);
    });
    panel.addEventListener('click', event => {
      const row = event.target.closest('[role="option"]');
      if (row) commit(state, Number(row.dataset.index));
    });
    select.addEventListener('change', () => sync(state));
    select.addEventListener('invalid', event => { event.preventDefault(); state.invalidReported = true; button.focus(); open(state); });
    new MutationObserver(schedule).observe(select, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled','hidden','selected','label','value','aria-label','aria-labelledby'] });
    for (const label of Array.from(select.labels || [])) label.addEventListener('click', event => {
      if (!event.target.closest('button')) { event.preventDefault(); button.focus(); }
    });
    sync(state, styleSnapshot);
  }

  function scan() {
    document.querySelectorAll('.collection-bar-inner').forEach(bar => {
      const toggles = Array.from(bar.children).filter(node => node.matches('.cmain-chip'));
      if (toggles.length > 1) {
        const group = document.createElement('span'); group.className = 'je-filter-toggles';
        toggles[0].before(group); group.append(...toggles);
      }
      const children = Array.from(bar.children);
      const search = children.findIndex(node => node.matches('.cmain-search'));
      const sort = children.findIndex(node => node.matches('.sort-form'));
      if (search >= 0 && sort > search) {
        const tools = document.createElement('div'); tools.className = 'je-filter-tools';
        children[search].before(tools); tools.append(...children.slice(search, sort + 1));
      }
    });
    // je-select-style-batch-20261103: snapshot computed strings before sync writes.
    const selects = Array.from(document.querySelectorAll('select'));
    const snapshots = new Map();
    for (const select of selects) {
      const state = states.get(select);
      if (!select.isConnected || (state && !state.wrapper.contains(select))) continue;
      if (!state && (select.multiple || select.size > 1 || select.hasAttribute('data-je-native-select') || select.hidden)) continue;
      const style = getComputedStyle(select);
      snapshots.set(select, { display: style.display, gridArea: style.gridArea });
    }
    for (const [select, state] of states) {
      if (!select.isConnected || !state.wrapper.contains(select)) {
        if (current === state) close(false);
        if (state.wrapper.isConnected) state.wrapper.replaceWith(...Array.from(state.wrapper.childNodes).filter(node => node !== state.button));
        state.panel.remove(); states.delete(select);
      } else sync(state, snapshots.get(select));
    }
    selects.forEach(select => bind(select, snapshots.get(select)));
  }
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; scan(); });
  }
  document.addEventListener('pointerdown', event => {
    if (current && !current.wrapper.contains(event.target) && !current.panel.contains(event.target)) close(false);
  }, true);
  document.addEventListener('focusin', event => {
    if (current && !current.wrapper.contains(event.target) && !current.panel.contains(event.target)) close(false);
  });
  document.addEventListener('click', schedule);
  document.addEventListener('change', schedule);
  document.addEventListener('reset', event => {
    for (const state of states.values()) if (state.select.form === event.target) state.invalidReported = false;
    setTimeout(schedule, 0);
  });
  document.addEventListener('scroll', event => { if (current && !current.panel.contains(event.target)) place(current); }, true);
  window.addEventListener('resize', schedule, { passive: true });
  window.visualViewport?.addEventListener('resize', () => { if (current) place(current); });
  window.addEventListener('pageshow', schedule);
  new MutationObserver(records => {
    if (records.some(record => Array.from(record.addedNodes).some(node => node.nodeType === 1 && !node.closest('.je-select__menu') && (node.matches('select') || node.querySelector('select'))) || Array.from(record.removedNodes).some(node => node.nodeType === 1 && (node.matches('select,.je-select') || node.querySelector('select'))))) schedule();
  }).observe(document.documentElement, { childList: true, subtree: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, { once: true });
  else scan();
})();
